import { Link } from 'react-router-dom'
import { ArrowUpRight, Lock } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { useCan } from '@/hooks/useCan'
import { useUiStore } from '@/stores/uiStore'

/**
 * What a plan limit looks like when you hit one.
 *
 * The whole 402 path existed already and stopped one step short. PlanGate
 * throws, the exception renders as 402 rather than 403 so no frontend code has
 * to string-match an error message, the axios layer builds a typed
 * PlanLimitError, AuthProvider hands it to the store — and then nothing read
 * the store. A shop on its five-hundredth product watched Save do nothing, with
 * no message, no explanation and no way to buy the thing that would fix it.
 *
 * Mounted in ProtectedRoute rather than AppShell, because THE POS SITS OUTSIDE
 * APPSHELL. `max_sales_per_month` is the limit a busy shop hits first and the
 * till is exactly where it would hit it, so a modal that only covers the
 * back-office screens would miss the one that matters.
 *
 * The message comes from the server. PlanLimitExceededException composes
 * "Your plan allows up to 500 products. Upgrade to add more." from the limit
 * key, so the wording lives next to the rule it describes and there is no
 * second copy here to drift from it.
 *
 * TWO KINDS OF 402 ARRIVE HERE and they are not the same sentence.
 *
 * A LIMIT is a quantity you have used up — 500 of 500 products. There is a
 * number, it applies to the next one only, and "you have reached" is true.
 *
 * A FEATURE is not on your plan at all — purchases, transfers, saved reports.
 * There is no number, nothing has been used up, and nothing is "reached". The
 * dialog said "You have reached a plan limit" over the top of the server's
 * own "Your plan does not include this feature", and then told somebody
 * opening a read-only screen that it "only stops the new one" — of what?
 *
 * `max` is what separates them: EnsureFeature aborts with no limit metadata.
 */
export function UpgradePrompt() {
  const prompt = useUiStore((s) => s.upgradePrompt)
  const close = useUiStore((s) => s.closeUpgradePrompt)

  // Whoever can act on this. A cashier hitting a limit mid-sale cannot buy
  // anything, and sending them to a billing page they are refused entry to
  // would be a second dead end on top of the first.
  const canBuy = useCan('billing.manage')

  if (prompt === null) return null

  const isLimit = prompt.max !== null

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) close()
      }}
      title={isLimit ? 'You have reached a plan limit' : 'Not on your plan'}
      description={prompt.message}
      size="sm"
    >
      <div className="flex flex-col gap-4">
        {isLimit && prompt.max !== null && (
          <div className="flex items-baseline justify-between rounded-lg bg-sunken px-3 py-2.5">
            <span className="text-sm text-ink-secondary">Used</span>
            <span className="tabular text-sm font-semibold">
              {prompt.current.toLocaleString()} of {prompt.max.toLocaleString()}
            </span>
          </div>
        )}

        {/* Said plainly: nothing already recorded is at risk. The first thing
            somebody wonders when a save is refused is whether they have lost
            what is on the screen — and for a feature, whether the data behind
            it has gone. It has not; only the screen is closed. */}
        <p className="text-sm leading-relaxed text-ink-secondary">
          {isLimit ? (
            <>
              Nothing you have already saved is affected. This only stops the new one
              {canBuy ? ' until you move to a larger plan.' : '.'}
            </>
          ) : (
            <>
              Everything already in your workspace is safe and stays where it is
              {canBuy ? '. Moving to a larger plan switches this on.' : '.'}
            </>
          )}
        </p>

        {canBuy ? (
          <div className="flex gap-2">
            <Button type="button" variant="secondary" className="flex-1" onClick={close}>
              Not now
            </Button>
            <Button asChild className="flex-1">
              <Link to="/billing" onClick={close}>
                See plans
                <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
              </Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-2.5 rounded-lg border border-border bg-sunken p-3">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} aria-hidden />
              <p className="text-xs leading-relaxed text-ink-secondary">
                Changing the plan needs the account owner. Ask them to look at Billing.
              </p>
            </div>

            <Button type="button" className="w-full" onClick={close}>
              Close
            </Button>
          </>
        )}
      </div>
    </Dialog>
  )
}
